import { zodResolver } from '@hookform/resolvers/zod';
import { FormContainer, notify, Text } from '@wyz/ui';
import { OFFER_TYPE, useBoolean } from '@wyz/utils';
import { debounce, isNil, sum } from 'lodash-es';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from '~/common/context';
import { useManageSearch } from '~/purchase/api';
import {
  PurchaseAddToCartFailureModal,
  PurchaseOfferErrorRow,
  PurchaseOfferSuccessRow,
  PurchaseTable,
} from '~/purchase/components';
import { CartUtils, MIN_QUANTITY } from '~/purchase/helpers';

import { ITyreSupplier } from '@wyz/types';
import { IUserCustomer } from '~/common/types';
import { usePurchaseCart } from '~/purchase/hooks';
import { getListQuantitySchema } from '~/purchase/schemas';
import {
  IBaseTyreOffer,
  IProductListState,
  ISearchTypeArgs,
} from '~/purchase/types';
import { useCallback } from 'react';

type Props = {
  product: IBaseTyreOffer;
  requestedQuantity: number;
  customer: IUserCustomer;
  searchType: ISearchTypeArgs['searchType'];
  searchQuery: IProductListState['searchQuery'];
  onUpdateQuery: (tyreOffer: IBaseTyreOffer) => IBaseTyreOffer | undefined;
};
function TyreGroupRow({
  product,
  requestedQuantity,
  customer,
  searchType,
  searchQuery,
  onUpdateQuery,
}: Props) {
  const purchaseCart = usePurchaseCart(customer.id, {
    onAddToCartFailed: () => addToCartFailureHandlers.setTrue(),
  });

  const manageSearch = useManageSearch<IBaseTyreOffer>({
    context: {
      customerId: customer.id,
      searchType,
    },
    refreshEan: { enabled: true },
  });

  const { cart } = purchaseCart;
  const quantitySchema = getListQuantitySchema(
    product.offers.map((offer) => ({
      quantity: offer.offerMetadata.availableQuantity,
      isParent:
        offer.offerMetadata.offerType === OFFER_TYPE.DISTRIBUTOR_PLATFORM,
    })),
    MIN_QUANTITY,
    requestedQuantity,
  );
  const defaultValues = product.offers.map(
    (offer) => offer.offerMetadata.proposedQuantity,
  );

  const methods = useForm({
    resolver: zodResolver(quantitySchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      quantity: defaultValues,
    },
  });

  const [showStockDetails, showStockDetailsHandlers] = useBoolean();

  const [addToCartFailureOpened, addToCartFailureHandlers] = useBoolean();
  const cartItems =
    cart.get.data?.data.items.filter(
      (item) => item.tyre.ean === product.tyre.ean,
    ) || [];

  const onUpdateQuantity = (supplier: ITyreSupplier) =>
    debounce((newQuantity?: number | null) => {
      // Update the cart ONLY if the user has already added this row to cart
      if (cartItems.length > 0) {
        const supplierCartItem = cartItems.find(
          (item) => item.supplier.id === supplier.id,
        );
        if (supplierCartItem) {
          cart.patch.mutate({
            itemId: supplierCartItem.id,
            quantity: newQuantity || 0,
          });
        }
      }
    }, 2000);

  const onRefreshTyre = useCallback(
    (ean: string) => {
      return new Promise<IBaseTyreOffer['offers']>((resolve, reject) => {
        if (isNil(searchQuery)) {
          return reject(new Error('No search query found'));
        }
        manageSearch.refreshEan.mutate(
          {
            ean: ean,
            baseQuery: searchQuery,
          },
          {
            onSuccess: (tyreOffer) => {
              if (isNil(tyreOffer) || isNil(searchQuery)) {
                return;
              }
              const updatedTyreOffer = onUpdateQuery(tyreOffer);
              if (updatedTyreOffer) {
                resolve(updatedTyreOffer.offers);
              }
            },
          },
        );
      });
    },
    [manageSearch.refreshEan, onUpdateQuery, searchQuery],
  );

  return (
    <React.Fragment>
      <FormContainer
        methods={methods}
        submitHandler={(formData) => {
          const quantities = formData.quantity.filter((value) => !isNil(value));
          const totalQuantityPerTyre = sum(quantities);
          if (totalQuantityPerTyre === 0) {
            notify.warning(
              <Text id='empty-quantity-warning'>
                <Trans i18nKey='app.purchase.cart.errors.empty_quantity' />
              </Text>,
              { id: 'toast-warning-purchase-cart-empty-quantity' },
            );
          } else {
            purchaseCart.addToCart.process(
              CartUtils.prepareAddToCartPayload(
                product.tyre,
                product.offers,
                quantities,
              ),
              {
                ean: product.tyre.ean,
                dimension: product.tyre.dimension.fullSize,
                brand: product.tyre.brand.name,
              },
              totalQuantityPerTyre,
              product.offers[0]?.offerMetadata.displayPrice ?? 0,
            );
          }
        }}
        id={product.tyre.ean}
      >
        <PurchaseTable>
          <tbody>
            {product.status === 'AVAILABLE_STOCK' ? (
              product.offers.map((offer, offerIndex) => {
                return (
                  <PurchaseOfferSuccessRow
                    key={`${product.tyre.ean}-${offer.supplier.id}-${offerIndex}`}
                    showCommon={offerIndex === 0}
                    offersCount={product.offers.length}
                    product={product}
                    offer={offer}
                    requestedQuantity={requestedQuantity}
                    isAdding={purchaseCart.addToCart.isLoading}
                    onUpdateQuantity={onUpdateQuantity}
                    offerIndex={offerIndex}
                    customer={customer}
                    onRefreshTyre={onRefreshTyre}
                    isRefreshingTyre={manageSearch.refreshEan.isLoading}
                    showStockDetails={showStockDetails}
                    toggleShowStockDetails={showStockDetailsHandlers.toggle}
                  />
                );
              })
            ) : (
              <PurchaseOfferErrorRow
                product={product}
                customer={customer}
                showCommon
                showStockDetails={showStockDetails}
                toggleShowStockDetails={showStockDetailsHandlers.toggle}
              />
            )}
          </tbody>
        </PurchaseTable>
      </FormContainer>
      {addToCartFailureOpened && (
        <PurchaseAddToCartFailureModal
          onClose={() => {
            addToCartFailureHandlers.setFalse();
            cart.get.refetch().catch(() => {});
          }}
          error={cart.add.error}
        />
      )}
    </React.Fragment>
  );
}

export default React.memo(TyreGroupRow);
